import React, { useEffect } from 'react';
import { VStack, HStack, Text, Box, View, Skeleton, Button } from 'native-base';

// Assets
import { theme } from '@breakroom-nx/assets';

// Classes & Types
import { UserAvatar, ChatBubble, IconButton } from '../../';
import { Notification } from '@breakroom-nx/classes';
import { NotificationType, timeFrom } from '@breakroom-nx/helpers';
import {
  useGetNotificationDataQuery,
  useMarkNotificationAsReadMutation,
} from '@breakroom-nx/redux';
import {
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
} from 'react-native-heroicons/outline';

/* eslint-disable-next-line */
export interface NotificationCellProps {
  notification: Notification;
}

export function NotificationCell({ notification }: NotificationCellProps) {
  const { data, isLoading } = useGetNotificationDataQuery(notification);
  const [markAsRead] = useMarkNotificationAsReadMutation();

  useEffect(() => {
    // console.log(`Notification: ${notification.title}`);
  });

  return (
    <HStack
      alignItems={'end'}
      space={5}
      rounded={'lg'}
      p={5}
      mb={5}
      bg={theme.colors.surface[200]}
      borderColor={notification.read ? 'none' : theme.colors.primary[400]}
      borderWidth={notification.read ? 0 : 2}
    >
      {isLoading ? (
        <VStack space={2} flex={2} alignItems={'flex-start'}>
          <Skeleton
            h={10}
            w={10}
            rounded="full"
            startColor={theme.colors.surface[400]}
          />
          <Skeleton h={5} rounded="lg" startColor={theme.colors.surface[400]} />
        </VStack>
      ) : (
        <>
          <VStack space={2} flex={2} alignItems={'flex-start'}>
            {/* Title */}
            <HStack
              alignItems={'center'}
              justifyContent={'space-between'}
              alignContent={'space-between'}
              width={'full'}
            >
              <HStack alignItems={'center'}>
                {data && (
                  <HStack space={3} alignItems={'center'}>
                    {data?.room ? (
                      <Box>
                        <ChatBubble
                          room={data.room}
                          showStreak={true}
                          key={data.room.id}
                        />
                        {data.user && (
                          <View position={'absolute'} right={0} bottom={0}>
                            <UserAvatar
                              uid={data.user.uid}
                              borderColor={'white'}
                              size={8}
                            />
                          </View>
                        )}
                      </Box>
                    ) : (
                      data?.user && (
                        <UserAvatar uid={data.user.uid} size={12} noBorder />
                      )
                    )}

                    <Text color={'white'} fontWeight={'bold'}>
                      {notification.type}
                    </Text>
                  </HStack>
                )}
                <Text color={theme.colors.white} fontSize={18} fontWeight={700}>
                  {notification.title}
                </Text>
              </HStack>

              {!notification.read && (
                <IconButton
                  disabled={notification.read}
                  icon={<EyeIcon color={theme.colors.primary[400]} size={20} />}
                  onPress={() => {
                    if (notification.uid) {
                      markAsRead(notification.uid);
                    }
                  }}
                  tooltipText={'Mark as Viewed'}
                />
              )}
            </HStack>
            {/* Message */}
            <Text color={theme.colors.surface[600]} fontSize={16} mb={1}>
              {data?.user && (
                <Text color={theme.colors.yellow[400]} fontWeight={800}>
                  {`${data.user.username} `}
                </Text>
              )}
              {notification.message}
              {data?.room && (
                <Text color={theme.colors.yellow[400]} fontWeight={800}>
                  {` ${data.room.title}`}
                </Text>
              )}
            </Text>
            {/* Actions (if any) */}
            {notification.actions && notification.actions.length > 0 && (
              <HStack space={2} mt={1}>
                {notification.actions.map((action, idx) => (
                  <Button
                    key={action.label + idx}
                    rounded={'full'}
                    opacity={0.75}
                    color={'primary.400'}
                    backgroundColor={'rgba(255, 255, 255, 0.07)'}
                    _hover={{
                      opacity: 1,
                    }}
                    _pressed={{
                      backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      opacity: 0.7,
                    }}
                  >
                    <Text color={theme.colors.surface[600]}>
                      {action.label}
                    </Text>
                  </Button>
                ))}
              </HStack>
            )}
            {/* Created/Updated */}
            <Text
              color={theme.colors.surface[500]}
              fontSize={14}
              fontWeight={400}
            >
              {timeFrom(notification.createdAt)}
              {notification.updatedAt &&
                ` • Updated ${timeFrom(notification.updatedAt)}`}
            </Text>
          </VStack>
        </>
      )}
    </HStack>
  );
}

export default NotificationCell;
// export default React.memo(NotificationInfoCell);
